import { useState } from "react";
import { useTranslation } from "react-i18next";
import { auth, establishSessionAfterLogin, getErrorCodeMessage } from "@envirohub/utils";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  PasswordInput,
  PASSWORD_MIN_LENGTH,
  toast,
} from "@envirohub/ui";
import { useChangePassword } from "@/api";
import { env } from "@/config/env";

export interface ChangePasswordCardProps {
  forced?: boolean;
  onCompleted?: () => void;
}

export function ChangePasswordCard({ forced = false, onCompleted }: ChangePasswordCardProps) {
  const { t } = useTranslation();
  const changePassword = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    newPassword.length >= PASSWORD_MIN_LENGTH &&
    newPassword === confirmPassword;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    changePassword.mutate(
      { currentPassword, newPassword },
      {
        onSuccess: () => {
          setIsRefreshing(true);
          establishSessionAfterLogin(env.VITE_API_BASE_URL)
            .then(() => {
              auth.clearMustResetPassword();
              toast.success(t("auth.changePassword.success"));
              setCurrentPassword("");
              setNewPassword("");
              setConfirmPassword("");
              onCompleted?.();
            })
            .catch((error) => {
              toast.error(getErrorCodeMessage(error, t));
            })
            .finally(() => {
              setIsRefreshing(false);
            });
        },
        onError: (error) => toast.error(getErrorCodeMessage(error, t)),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {forced ? t("auth.forcedChangePasswordTitle") : t("auth.changePassword.title")}
        </CardTitle>
        {forced ? (
          <CardDescription>{t("auth.forcedChangePasswordDescription")}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent>
        <form className="flex max-w-md flex-col gap-4" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            <Label htmlFor="current-password">{t("auth.changePassword.current")}</Label>
            <PasswordInput
              id="current-password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="new-password">{t("auth.changePassword.new")}</Label>
            <PasswordInput
              id="new-password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <p className="text-body-sm text-surface-500">
              {t("auth.changePassword.minLength", { count: PASSWORD_MIN_LENGTH })}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="confirm-password">{t("auth.changePassword.confirm")}</Label>
            <PasswordInput
              id="confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {mismatch && (
              <p className="text-body-sm text-error">{t("auth.changePassword.mismatch")}</p>
            )}
          </div>
          <div>
            <Button
              type="submit"
              disabled={!canSubmit}
              loading={changePassword.isPending || isRefreshing}
            >
              {t("auth.changePassword.submit")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
